import { useMemo, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWorkouts } from './useWorkouts';
import { fsGetUnlockedAchievements, fsMarkAchievementUnlocked } from '../services/firestoreDb';

export const ALL_ACHIEVEMENTS = [
  { id: 'first_workout', icon: '🏃', title: 'First Step',        desc: 'Log your first workout',    color: '#10B981' },
  { id: 'workouts_5',    icon: '⚡', title: 'Warming Up',         desc: 'Log 5 workouts',            color: '#3B82F6' },
  { id: 'workouts_25',   icon: '💪', title: 'Getting Serious',    desc: 'Log 25 workouts',           color: '#8B5CF6' },
  { id: 'workouts_100',  icon: '🏆', title: 'Century Club',       desc: 'Log 100 workouts',          color: '#F59E0B' },
  { id: 'streak_3',      icon: '🔥', title: 'On Fire',            desc: '3-day streak',              color: '#EF4444' },
  { id: 'streak_7',      icon: '⚔️', title: 'Week Warrior',       desc: '7-day streak',              color: '#EC4899' },
  { id: 'streak_14',     icon: '🗡️', title: 'Fortnight Fighter',  desc: '14-day streak',             color: '#06B6D4' },
  { id: 'streak_30',     icon: '👑', title: 'Monthly Grind',      desc: '30-day streak',             color: '#EAB308' },
  { id: 'distance_10',   icon: '🗺️', title: 'Explorer',          desc: 'Walk 10 km total',          color: '#059669' },
  { id: 'distance_100',  icon: '🌍', title: 'Road Warrior',       desc: 'Walk 100 km total',         color: '#0891B2' },
  { id: 'gym_20',        icon: '🏋️', title: 'Gym Rat',           desc: '20 gym sessions',           color: '#7C3AED' },
  { id: 'badminton_10',  icon: '🏸', title: 'Badminton Pro',      desc: '10 badminton sessions',     color: '#F97316' },
  { id: 'skating_10',    icon: '⛸️', title: 'Ice Royalty',        desc: '10 skating sessions',       color: '#0EA5E9' },
  { id: 'all_rounder',   icon: '🌟', title: 'All-Rounder',        desc: 'Try all 4 activities',      color: '#F59E0B' },
  { id: 'early_bird',    icon: '🌅', title: 'Early Bird',         desc: 'Log a morning workout',     color: '#FBBF24' },
];

export function useAchievements() {
  const { user } = useAuth();
  const uid = user?.id;
  const { workouts, totalDistance, currentStreak, longestStreak } = useWorkouts();
  const [unlockedDates, setUnlockedDates] = useState({});
  const syncedRef = useRef(new Set());

  const unlocked = useMemo(() => {
    const ids = new Set();
    if (!workouts.length) return ids;

    const n = workouts.length;
    const types = new Set(workouts.map(w => w.type));
    const bestStreak = Math.max(currentStreak, longestStreak);

    if (n >= 1)   ids.add('first_workout');
    if (n >= 5)   ids.add('workouts_5');
    if (n >= 25)  ids.add('workouts_25');
    if (n >= 100) ids.add('workouts_100');

    if (bestStreak >= 3)  ids.add('streak_3');
    if (bestStreak >= 7)  ids.add('streak_7');
    if (bestStreak >= 14) ids.add('streak_14');
    if (bestStreak >= 30) ids.add('streak_30');

    if (totalDistance >= 10)  ids.add('distance_10');
    if (totalDistance >= 100) ids.add('distance_100');

    if (workouts.filter(w => w.type === 'gym').length >= 20)       ids.add('gym_20');
    if (workouts.filter(w => w.type === 'badminton').length >= 10) ids.add('badminton_10');
    if (workouts.filter(w => w.type === 'skating').length >= 10)   ids.add('skating_10');

    if (['walking', 'gym', 'badminton', 'skating'].every(t => types.has(t))) ids.add('all_rounder');

    const hasEarlyBird = workouts.some(w => {
      if (!w.date || !w.date.includes('T')) return false;
      const h = new Date(w.date).getHours();
      return h >= 5 && h < 9;
    });
    if (hasEarlyBird) ids.add('early_bird');

    return ids;
  }, [workouts, totalDistance, currentStreak, longestStreak]);

  // Sync newly-unlocked achievements to Firestore (for cross-device state and
  // the achievement timeline's unlock dates). Purely additive — the `unlocked`
  // Set above remains the local, always-available source of truth.
  useEffect(() => {
    if (!uid) return;
    let active = true;
    fsGetUnlockedAchievements(uid)
      .then((remote) => {
        if (!active || !remote) return;
        const dates = {};
        remote.forEach((r) => {
          dates[r.id] = r.unlockedAt || null;
          syncedRef.current.add(r.id);
        });
        setUnlockedDates(dates);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    const now = new Date().toISOString();
    unlocked.forEach((id) => {
      if (syncedRef.current.has(id)) return;
      syncedRef.current.add(id);
      setUnlockedDates((prev) => (prev[id] ? prev : { ...prev, [id]: now }));
      fsMarkAchievementUnlocked(uid, id).catch(() => {});
    });
  }, [uid, unlocked]);

  return { achievements: ALL_ACHIEVEMENTS, unlocked, unlockedDates };
}
